import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  Users, Home, MapPin, CheckCircle2, Clock, XCircle, Ban, Star, Download,
  ListChecks, ShieldCheck, AlertTriangle, TrendingUp, Activity, Filter,
  User, GraduationCap, Heart, UsersRound, CreditCard, Wallet, HandCoins,
  Calendar, ArrowRight, ArrowUp, ArrowDown, RefreshCw, BarChart3, Info,
  FileSpreadsheet, FileType, Sparkles,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";

interface RegBreakRow {
  id: string;
  region: string | null;
  category: string | null;
  payment_choice: string | null;
  is_student: boolean | null;
}

interface RegionRow {
  region_id: string; region_name: string; families: number; total_members: number;
  adults: number; teens: number; children_6_12: number; children_0_5: number;
}
interface CityRow {
  region: string; city: string; families: number; total_members: number;
  adults: number; teens: number; children_6_12: number; children_0_5: number;
  confirmed: number; pending: number; rejected: number; cancelled: number;
}
interface DailyRow { day: string; registrations: number; members: number }
interface CancelRow { region: string; pending: number; approved: number; rejected: number; total: number }
interface FeedbackRow {
  region: string; total: number;
  avg_food: number; avg_venue: number; avg_speakers: number;
  avg_timekeeping: number; avg_program: number;
}
interface FeedbackDetailRow {
  id: string;
  created_at: string;
  region: string | null;
  state: string | null;
  city: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  attended: string | null;
  rating_food: number | null;
  rating_venue: number | null;
  rating_speakers: number | null;
  rating_timekeeping: number | null;
  rating_program: number | null;
  rating_registration_process: number | null;
  feedback_food: string | null;
  feedback_venue: string | null;
  feedback_speakers: string | null;
  feedback_timekeeping: string | null;
  feedback_program: string | null;
  feedback_registration_process: string | null;
  suggestions: string | null;
}

interface SourceRow {
  source_bucket: string; registration_source: string | null;
  utm_source: string | null; utm_medium: string | null; utm_campaign: string | null;
  families: number; total_members: number;
}
interface RecentRegRow {
  id: string; first_name: string; last_name: string; region: string;
  city: string | null; total_members: number; payment_status: string;
  created_at: string; updated_at: string;
}
interface RecentCancelRow {
  id: string; region: string; status: string; created_at: string;
  registration_id: string | null;
}

const COLORS = ["hsl(var(--primary))", "hsl(var(--ministry-cyan))", "#F59E0B", "#10B981", "#EF4444", "#8B5CF6"];

const STATUS_META = {
  confirmed: { label: "Confirmed", color: "#10B981", bg: "bg-emerald-500", text: "text-emerald-600" },
  pending:   { label: "Pending",   color: "#F59E0B", bg: "bg-amber-500",   text: "text-amber-600" },
  cancelled: { label: "Cancelled", color: "#F43F5E", bg: "bg-rose-500",    text: "text-rose-600" },
  rejected:  { label: "Rejected",  color: "#EF4444", bg: "bg-red-500",     text: "text-red-600" },
} as const;

const YEAR_TABS: { year: number; slug: string; label: string; status: "current" | "future" | "past" }[] = [
  { year: 2026, slug: "regional-conference-2026", label: "2026 (Current)", status: "current" },
  { year: 2027, slug: "regional-conference-2027", label: "2027", status: "future" },
  { year: 2028, slug: "regional-conference-2028", label: "2028", status: "future" },
  { year: 2029, slug: "regional-conference-2029", label: "2029", status: "future" },
  { year: 2030, slug: "regional-conference-2030", label: "2030", status: "future" },
];

const normalizeRegionName = (value: unknown) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/\bregion\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const regionMatches = (value: unknown, target: unknown) => {
  const current = normalizeRegionName(value);
  const expected = normalizeRegionName(target);
  if (!current || !expected) return false;
  return current === expected || current.startsWith(`${expected} `) || expected.startsWith(`${current} `);
};

// Collapse region label variants ("Southern" / "Southern Region") into a single
// weighted row so the feedback summary never lists the same region twice.
const mergeFeedbackRows = (rows: FeedbackRow[]): FeedbackRow[] => {
  const map = new Map<string, { label: string; total: number; sums: Record<string, number> }>();
  const keys = ["avg_food", "avg_venue", "avg_speakers", "avg_timekeeping", "avg_program"] as const;
  rows.forEach(r => {
    const key = normalizeRegionName(r.region) || String(r.region ?? "Unknown");
    const n = Number(r.total || 0);
    const existing = map.get(key);
    const label = String(r.region ?? "Unknown").replace(/\s*region\s*$/i, "").trim() + " Region";
    const entry = existing ?? { label, total: 0, sums: { avg_food: 0, avg_venue: 0, avg_speakers: 0, avg_timekeeping: 0, avg_program: 0 } };
    entry.total += n;
    keys.forEach(k => { entry.sums[k] += Number(r[k] || 0) * n; });
    map.set(key, entry);
  });
  return Array.from(map.values())
    .map(e => {
      const out: any = { region: e.label, total: e.total };
      keys.forEach(k => { out[k] = e.total ? Number((e.sums[k] / e.total).toFixed(2)) : null; });
      return out as FeedbackRow;
    })
    .sort((a, b) => a.region.localeCompare(b.region));
};


export const ConferenceAnalyticsDashboard = () => {
  const [activeSlug, setActiveSlug] = useState<string>(YEAR_TABS[0].slug);
  const { adminUser, loading: authLoading } = useAdminAuth();
  const [scopedRegion, setScopedRegion] = useState<string | null>(null);
  const [authTimedOut, setAuthTimedOut] = useState(false);
  // Never impersonate a region here. If the admin row cannot be resolved we
  // surface an error instead of silently locking the dashboard to one region.
  const effectiveAdminUser = adminUser;
  const adminRole = effectiveAdminUser?.role || null;
  const isGlobalViewer = adminRole === "global_admin" || adminRole === "president";
  const isScoped = Boolean(effectiveAdminUser && !isGlobalViewer && effectiveAdminUser.region_id);
  const scopeLabel = scopedRegion || "Assigned region";

  useEffect(() => {
    if (!authLoading && !adminUser) {
      // Give auth a short grace window: duplicate Supabase auth events can
      // briefly report loading=false before the admin row resolves.
      const timer = window.setTimeout(() => {
        console.warn("[analytics-scope] Admin user unavailable after auth load");
        setAuthTimedOut(true);
      }, 2500);
      return () => window.clearTimeout(timer);
    }
    setAuthTimedOut(false);
  }, [authLoading, adminUser]);



  // Resolve region name for regional/kit admins (used to lock the dashboard scope)
  useEffect(() => {
    let cancelled = false;

    const resolve = async () => {
      if (!effectiveAdminUser) { setScopedRegion(null); return; }
      if (effectiveAdminUser.role === "global_admin" || effectiveAdminUser.role === "president") {
        setScopedRegion(null);
        return;
      }

      const embeddedRegion = (effectiveAdminUser as any)?.region_name || (effectiveAdminUser as any)?.region?.name || (effectiveAdminUser as any)?.regions?.name;
      if (embeddedRegion) {
        if (!cancelled) setScopedRegion(embeddedRegion);
        return;
      }

      if (!effectiveAdminUser.region_id) {
        // No region assigned: do not guess a region — leave unscoped.
        console.warn("[analytics-scope] Admin has no region_id; leaving scope unset");
        if (!cancelled) setScopedRegion(null);
        return;
      }


      try {
        const { data, error } = await supabase
          .from("regions")
          .select("name")
          .eq("id", effectiveAdminUser.region_id)
          .maybeSingle();

        if (error) console.warn("[analytics-scope] regions lookup error", error);

        if (data?.name) {
          if (!cancelled) setScopedRegion(data.name as string);
          return;
        }
      } catch (err) {
        console.error("[analytics-scope] regions lookup threw", err);
      }

      try {
        const { data: rpcData, error: rpcError } = await (supabase as any).rpc("get_admin_user_info", {
          user_id_param: effectiveAdminUser.auth_user_id,
        });
        if (rpcError) console.warn("[analytics-scope] rpc error", rpcError);
        const rpcRegion = Array.isArray(rpcData) ? rpcData[0]?.region_name : rpcData?.region_name;
        if (rpcRegion) {
          if (!cancelled) setScopedRegion(rpcRegion);
          return;
        }
      } catch (err) {
        console.error("[analytics-scope] rpc threw", err);
      }

      // Final fallback so the dashboard never hangs: unblock with region_id
      // so filtering can still run (regionMatches will simply not match, but
      // downstream loaders won't be blocked and the user sees an actionable UI).
      console.warn("[analytics-scope] Falling back to region_id as scope key", effectiveAdminUser.region_id);
      if (!cancelled) setScopedRegion(String(effectiveAdminUser.region_id));
    };

    void resolve();

    // Hard safety: never hang the dashboard on scope resolution.
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      if (!effectiveAdminUser?.region_id) return;
      setScopedRegion((prev) => {
        if (prev) return prev;
        console.warn("[analytics-scope] Scope resolution timed out; unblocking dashboard");
        return String(effectiveAdminUser.region_id);
      });
    }, 4000);

    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [effectiveAdminUser]);

  const headerTitle = isScoped
    ? `Conference Analytics — ${scopeLabel}`
    : "Conference Analytics";
  const headerSubtitle = isScoped
    ? `Live registration metrics for ${scopeLabel}. Data is automatically scoped to your assigned region.`
    : "Live registration, cancellation, and feedback metrics across every region for the OFUESI Annual Regional Conference.";

  if (authLoading && !effectiveAdminUser) {
    return <div className="p-8 text-muted-foreground">Loading admin scope…</div>;
  }

  if (!effectiveAdminUser) {
    return (
      <div className="p-8 text-sm text-muted-foreground">
        We could not load your admin profile{authTimedOut ? " (session lookup timed out)" : ""}. Please refresh
        the page or sign in again.
      </div>
    );
  }




  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-2xl font-bold">{headerTitle}</h2>
            {isScoped && (
              <Badge variant="secondary" className="bg-sky-50 text-sky-700 border border-sky-200">
                <ShieldCheck className="w-3 h-3 mr-1" />
                Scoped to {scopeLabel}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">{headerSubtitle}</p>
        </div>
      </div>

      <Tabs value={activeSlug} onValueChange={setActiveSlug}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          {YEAR_TABS.map(t => (
            <TabsTrigger key={t.slug} value={t.slug} className="flex items-center gap-2">
              {t.label}
              {t.status === "future" && <Badge variant="outline" className="text-[10px]">Upcoming</Badge>}
            </TabsTrigger>
          ))}
        </TabsList>
        {YEAR_TABS.map(t => (
          <TabsContent key={t.slug} value={t.slug} className="mt-6">
            <YearPanel
              slug={t.slug}
              year={t.year}
              status={t.status}
              scopedRegion={scopedRegion}
              isScoped={isScoped}
              adminRole={adminRole}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

function YearPanel({
  slug, year, status, scopedRegion, isScoped, adminRole,
}: { slug: string; year: number; status: string; scopedRegion: string | null; isScoped: boolean; adminRole: string | null }) {
  const [loading, setLoading] = useState(true);
  const [regionRows, setRegionRows] = useState<RegionRow[]>([]);
  const [cityRows, setCityRows] = useState<CityRow[]>([]);
  const [dailyRows, setDailyRows] = useState<DailyRow[]>([]);
  const [cancelRows, setCancelRows] = useState<CancelRow[]>([]);
  const [feedbackRows, setFeedbackRows] = useState<FeedbackRow[]>([]);
  const [feedbackDetails, setFeedbackDetails] = useState<FeedbackDetailRow[]>([]);

  const [sourceRows, setSourceRows] = useState<SourceRow[]>([]);
  const [recentRegs, setRecentRegs] = useState<RecentRegRow[]>([]);
  const [recentCancels, setRecentCancels] = useState<RecentCancelRow[]>([]);
  const [regBreakRows, setRegBreakRows] = useState<RegBreakRow[]>([]);
  const [scholarshipCount, setScholarshipCount] = useState<number>(0);

  // Filters
  const [regionFilter, setRegionFilter] = useState<string>("all"); // global only
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [regionView, setRegionView] = useState<"bar" | "table" | "percent">("bar");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const cityRegionArg = isScoped ? scopedRegion : null;
        const [r, c, d, can, fb, src, recReg, recCan, regBreak, sch] = await Promise.all([
          (supabase as any).rpc("public_region_counts", { p_slug: slug }),
          (supabase as any).rpc("admin_city_counts", { p_region: cityRegionArg, p_slug: slug }),
          (supabase as any).rpc("admin_daily_counts", { p_region: cityRegionArg, p_days: 60 }),
          (supabase as any).rpc("admin_cancellation_counts", { p_slug: slug }),
          (supabase as any).rpc("admin_feedback_counts", { p_slug: slug }),
          (supabase as any).rpc("admin_registration_source_counts", { p_slug: slug }),
          (() => {
            let q = supabase.from("conference_registrations")
              .select("id, first_name, last_name, region, city, total_members, payment_status, created_at, updated_at")
              .eq("conference_slug", slug)
              .order("created_at", { ascending: false })
              .limit(20);
            if (isScoped && scopedRegion) q = q.eq("region", scopedRegion);
            return q;
          })(),
          (() => {
            let q = supabase.from("registration_cancellation_requests")
              .select("id, region, status, created_at, registration_id")
              .order("created_at", { ascending: false })
              .limit(20);
            if (isScoped && scopedRegion) q = q.eq("region", scopedRegion);
            return q;
          })(),
          (() => {
            let q = (supabase as any).from("conference_registrations")
              .select("id, region, category, payment_choice, is_student, category_price, conference_fee, amount_paid, amount_due, payment_status, needs_scholarship")
              .eq("conference_slug", slug)
              .limit(5000);
            if (isScoped && scopedRegion) q = q.eq("region", scopedRegion);
            return q;
          })(),
          (() => {
            let q = (supabase as any).from("conference_registrations")
              .select("id", { count: "exact", head: true })
              .eq("conference_slug", slug)
              .eq("needs_scholarship", true);
            if (isScoped && scopedRegion) q = q.eq("region", scopedRegion);
            return q;
          })(),
        ]);
        if (cancelled) return;

        // Apply scoping client-side as a defense in depth. Regional/kit admins
        // must never see any other region's data, even if a shared RPC returns
        // extra rows.
        const scope = <T extends { region?: string; region_name?: string }>(rows: T[]) => {
          if (!isScoped) return rows;
          return rows.filter(x => regionMatches(x.region ?? x.region_name, scopedRegion));
        };

        setRegionRows(scope((r.data || []) as RegionRow[]));
        setCityRows(scope((c.data || []) as CityRow[]));
        setDailyRows((d.data || []) as DailyRow[]);
        setCancelRows(scope((can.data || []) as CancelRow[]));
        setFeedbackRows(mergeFeedbackRows(scope((fb.data || []) as FeedbackRow[])));
        // Source breakdown RPC has no region parameter — always drop it for
        // scoped admins so we never leak cross-region traffic sources.
        setSourceRows(isScoped ? [] : ((src.data || []) as SourceRow[]));
        setRecentRegs(scope(((recReg as any).data || []) as RecentRegRow[]));
        setRecentCancels(scope(((recCan as any).data || []) as RecentCancelRow[]));
        setRegBreakRows(scope(((regBreak as any).data || []) as RegBreakRow[]));
        setScholarshipCount(Number((sch as any)?.count || 0));
      } catch (e) {
        console.error("Analytics load error", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, isScoped, scopedRegion]);

  // Detailed feedback responses (all comments + per-topic ratings)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await (supabase as any)
          .from("conference_feedback")
          .select("id, created_at, region, state, city, first_name, last_name, email, attended, rating_food, rating_venue, rating_speakers, rating_timekeeping, rating_program, rating_registration_process, feedback_food, feedback_venue, feedback_speakers, feedback_timekeeping, feedback_program, feedback_registration_process, suggestions")
          .eq("conference_slug", slug)
          .order("created_at", { ascending: false })
          .limit(1000);
        if (error) throw error;
        if (cancelled) return;
        const rows = (data || []) as FeedbackDetailRow[];
        setFeedbackDetails(isScoped ? rows.filter(x => regionMatches(x.region, scopedRegion)) : rows);
      } catch (e) {
        console.error("Feedback detail load error", e);
        if (!cancelled) setFeedbackDetails([]);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, isScoped, scopedRegion]);


  // Filtered city rows (for regional admin: already scoped via RPC; for global: apply UI filters)
  const filteredCityRows = useMemo(() => {
    let rows = cityRows;
    if (!isScoped && regionFilter !== "all") rows = rows.filter(r => r.region === regionFilter);
    return rows;
  }, [cityRows, isScoped, regionFilter]);

  const totals = useMemo(() => filteredCityRows.reduce(
    (acc, x) => ({
      families: acc.families + Number(x.families || 0),
      total_members: acc.total_members + Number(x.total_members || 0),
      adults: acc.adults + Number(x.adults || 0),
      teens: acc.teens + Number(x.teens || 0),
      children_6_12: acc.children_6_12 + Number(x.children_6_12 || 0),
      children_0_5: acc.children_0_5 + Number(x.children_0_5 || 0),
      confirmed: acc.confirmed + Number(x.confirmed || 0),
      pending: acc.pending + Number(x.pending || 0),
      rejected: acc.rejected + Number(x.rejected || 0),
      // NOTE: do NOT sum x.cancelled here — admin_city_counts repeats the
      // region-wide approved-cancellation count on every city row, which
      // inflates the total. We derive cancelled from cancelTotals.approved below.
    }),
    { families: 0, total_members: 0, adults: 0, teens: 0, children_6_12: 0, children_0_5: 0, confirmed: 0, pending: 0, rejected: 0 }
  ), [filteredCityRows]);

  const cancelTotals = useMemo(() => cancelRows.reduce(
    (a, x) => ({
      pending: a.pending + Number(x.pending || 0),
      approved: a.approved + Number(x.approved || 0),
      rejected: a.rejected + Number(x.rejected || 0),
      total: a.total + Number(x.total || 0),
    }),
    { pending: 0, approved: 0, rejected: 0, total: 0 }
  ), [cancelRows]);

  // Trustworthy per-region approved-cancellations count (from admin_cancellation_counts).
  const cancelledCount = cancelTotals.approved;
  const cancelledByRegion = useMemo(() => {
    const m: Record<string, number> = {};
    cancelRows.forEach(r => { m[r.region] = Number(r.approved || 0); });
    return m;
  }, [cancelRows]);

  const totalStatus = totals.confirmed + totals.pending + cancelledCount + totals.rejected;

  const totalRegistrations = totals.families;
  const reconciles = totalStatus === totalRegistrations;

  const citiesReached = useMemo(
    () => new Set(filteredCityRows.map(c => `${c.region}|${c.city}`)).size,
    [filteredCityRows]
  );

  const ageData = [
    { name: "Adults (18+)", value: totals.adults },
    { name: "Teens (13–17)", value: totals.teens },
    { name: "Children (6–12)", value: totals.children_6_12 },
    { name: "Children (0–5)", value: totals.children_0_5 },
  ];
  const ageTotal = ageData.reduce((s, a) => s + a.value, 0);

  // Executive summary helper (hook must run before any early return)
  const topRegion = useMemo(() => {
    const rows = regionRows
      .map(r => ({ name: r.region_name.replace(" Region", ""), members: Number(r.total_members) || 0 }))
      .sort((a, b) => b.members - a.members);
    const total = rows.reduce((s, r) => s + r.members, 0);
    if (!rows.length || !total) return null;
    return { ...rows[0], pct: Math.round((rows[0].members / total) * 100) };
  }, [regionRows]);

  // Available regions for the global filter (hook must run before any early return)
  const allRegionNames = useMemo(() => {
    const s = new Set<string>();
    cityRows.forEach(c => s.add(c.region));
    regionRows.forEach(r => s.add(r.region_name));
    return Array.from(s).sort();
  }, [cityRows, regionRows]);

  // Filtered registration breakdown rows (region filter for global admins, scoped for regional)
  const filteredRegBreak = useMemo(() => {
    let rows = regBreakRows;
    if (!isScoped && regionFilter !== "all") rows = rows.filter(r => r.region === regionFilter);
    return rows;
  }, [regBreakRows, isScoped, regionFilter]);

  // Category aggregates (Individuals / Students / Couples / Families)
  const categoryAgg = useMemo(() => {
    const buckets = { individual: 0, student: 0, couple: 0, family: 0 };
    filteredRegBreak.forEach(r => {
      const cat = (r.category || "").toLowerCase();
      if (cat === "individual") {
        if (r.is_student) buckets.student += 1;
        else buckets.individual += 1;
      } else if (cat === "student") buckets.student += 1;
      else if (cat === "couple") buckets.couple += 1;
      else if (cat === "family") buckets.family += 1;
    });
    const total = buckets.individual + buckets.student + buckets.couple + buckets.family;
    return { ...buckets, total };
  }, [filteredRegBreak]);

  // Revenue & family-tier aggregates
  const revenueAgg = useMemo(() => {
    let total = 0, paid = 0, due = 0, scholarshipAmt = 0;
    const familyTiers: Record<string, number> = { "125": 0, "250": 0, "350": 0 };
    filteredRegBreak.forEach((r: any) => {
      const fee = Number(r.category_price ?? r.conference_fee ?? 0);
      const rawPaid = Number(r.amount_paid ?? 0);
      const rawDue = r.amount_due != null ? Number(r.amount_due) : null;
      const status = String(r.payment_status ?? "").toLowerCase();
      // Derive paid amount: prefer explicit amount_paid; otherwise infer from
      // status (confirmed => full fee) or from (fee - amount_due) when due is set.
      let pAmt = rawPaid;
      if (pAmt <= 0) {
        if (status === "confirmed") pAmt = fee;
        else if (rawDue != null) pAmt = Math.max(fee - rawDue, 0);
      }
      // Derive due amount consistently with the derived paid amount.
      const dAmt = rawDue != null ? rawDue : Math.max(fee - pAmt, 0);
      total += fee; paid += pAmt; due += dAmt;
      if (r.needs_scholarship) scholarshipAmt += fee;
      if ((r.category || "").toLowerCase() === "family") {
        const key = String(Math.round(fee));
        if (familyTiers[key] != null) familyTiers[key] += 1;
      }
    });
    return { total, paid, due, familyTiers, scholarshipAmt };
  }, [filteredRegBreak]);

  // Payment method aggregates
  const paymentAgg = useMemo(() => {
    const buckets = { paid_now: 0, pay_later: 0, in_person: 0, other: 0 };
    filteredRegBreak.forEach(r => {
      const p = (r.payment_choice || "").toLowerCase();
      if (p === "zelle" || p === "online" || p === "paid" || p === "free") buckets.paid_now += 1;
      else if (p === "pay_later" || p === "later") buckets.pay_later += 1;
      else if (p === "in_person" || p === "cash" || p === "pay_in_person") buckets.in_person += 1;
      else if (p) buckets.other += 1;
    });
    const tracked = buckets.paid_now + buckets.pay_later + buckets.in_person + buckets.other;
    return { ...buckets, tracked, total: filteredRegBreak.length };
  }, [filteredRegBreak]);

  // Simple growth trend (last 7d vs prior 7d) — must be declared before any early return
  const trend = useMemo(() => {
    if (!dailyRows.length) return null;
    const sorted = [...dailyRows].sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
    const last7 = sorted.slice(-7).reduce((s, d) => s + Number(d.registrations || 0), 0);
    const prior7 = sorted.slice(-14, -7).reduce((s, d) => s + Number(d.registrations || 0), 0);
    if (prior7 === 0 && last7 === 0) return null;
    if (prior7 === 0) return { pct: 100, dir: "up" as const };
    const delta = ((last7 - prior7) / prior7) * 100;
    return { pct: Math.round(Math.abs(delta)), dir: (delta >= 0 ? "up" : "down") as "up" | "down" };
  }, [dailyRows]);

  const empty = totalRegistrations === 0 && cancelTotals.total === 0 && feedbackRows.length === 0;
  if (loading) return <div className="p-8 text-muted-foreground">Loading {year} analytics…</div>;
  if (empty && status === "future") {
    return (
      <Card>
        <CardContent className="p-10 text-center text-muted-foreground">
          <p className="text-lg font-medium mb-1">No data yet for {year}</p>
          <p className="text-sm">Registrations, cancellations and feedback for the {year} conference will appear here once collection begins.</p>
        </CardContent>
      </Card>
    );
  }

  // KPI definitions
  const kpis = [
    { key: "total", label: "Total Registrations", value: totalRegistrations, icon: ListChecks,
      hint: "All submitted registrations regardless of status.", tone: "text-primary", href: "/admin/dashboard/registrations" },
    { key: "confirmed", label: "Paid", value: totals.confirmed, icon: CheckCircle2,
      hint: "Payment verified and seat secured.", tone: "text-emerald-600", href: "/admin/dashboard/registrations?payment=confirmed" },
    { key: "pending", label: "Not Paid", value: totals.pending, icon: Clock,
      hint: "Awaiting payment confirmation.", tone: "text-amber-600", href: "/admin/dashboard/registrations?payment=pending" },
    { key: "cancelled", label: "Cancellations", value: cancelledCount, icon: Ban,
      hint: `Approved cancellation requests (${cancelTotals.pending} pending).`, tone: "text-rose-600", href: "/admin/dashboard/cancellations" },
    { key: "scholarship", label: "Scholarship Requests", value: scholarshipCount, icon: ListChecks,
      hint: "Registrations needing scholarship review.", tone: "text-amber-700", href: "/admin/dashboard/registrations?scholarship=1" },
    { key: "rejected", label: "Rejected Registrations", value: totals.rejected, icon: XCircle,
      hint: "Marked as rejected by an admin.", tone: "text-red-600", href: "/admin/dashboard/registrations?payment=rejected" },
    { key: "cities", label: "Cities Reached", value: citiesReached, icon: MapPin,
      hint: "Distinct cities with at least one registration.", tone: "text-sky-600" },
    { key: "families", label: "Families Registered", value: categoryAgg.family, icon: Home,
      hint: "Registrations in the Family category only.", tone: "text-indigo-600", href: "/admin/dashboard/registrations?category=family" },
    { key: "attendees", label: "Total Attendees", value: totals.total_members, icon: Users,
      hint: "Sum of every person across all registrations.", tone: "text-violet-600" },
    { key: "revenue", label: "Total Revenue", value: `$${revenueAgg.total.toLocaleString()}` as any, icon: ListChecks,
      hint: "Sum of calculated registration fees.", tone: "text-emerald-700" },
    { key: "paid_rev", label: "Paid Revenue", value: `$${revenueAgg.paid.toLocaleString()}` as any, icon: CheckCircle2,
      hint: "Confirmed payments received.", tone: "text-emerald-600", href: "/admin/dashboard/registrations?payment=confirmed" },
    { key: "due_rev", label: "Outstanding", value: `$${revenueAgg.due.toLocaleString()}` as any, icon: Clock,
      hint: "Fees owed by registered families.", tone: "text-amber-600", href: "/admin/dashboard/registrations?payment=pending" },
    { key: "scholarship_amt", label: "Scholarship Amount", value: `$${revenueAgg.scholarshipAmt.toLocaleString()}` as any, icon: HandCoins,
      hint: "Total fees for scholarship-request registrations.", tone: "text-amber-700", href: "/admin/dashboard/registrations?scholarship=1" },
  ];

  const execSummary = (() => {
    if (totals.total_members === 0) return `No attendees registered yet for ${year}.`;
    const parts = [
      `${totals.total_members} attendees`,
      `across ${citiesReached} ${citiesReached === 1 ? "city" : "cities"}`,
    ];
    const breakdown = `${totals.confirmed} confirmed, ${totals.pending} pending, ${cancelledCount} cancelled, and ${totals.rejected} rejected`;
    const tail = (!isScoped && topRegion)
      ? ` ${topRegion.name} Region accounts for ${topRegion.pct}% of attendees.`
      : "";
    return `${parts.join(" ")} registered. ${breakdown}.${tail}`;
  })();

  // Section insights
  const pctOf = (v: number, t: number) => (t ? Math.round((v / t) * 100) : 0);

  const categoryRanked = [
    { key: "individual", label: "Individuals", value: categoryAgg.individual, icon: User, color: "#0EA5E9" },
    { key: "student",    label: "Students",    value: categoryAgg.student,    icon: GraduationCap, color: "#8B5CF6" },
    { key: "couple",     label: "Couples",     value: categoryAgg.couple,     icon: Heart, color: "#EC4899" },
    { key: "family",     label: "Families",    value: categoryAgg.family,     icon: UsersRound, color: "#F59E0B" },
  ].sort((a, b) => b.value - a.value);

  const categorySummary = categoryAgg.total === 0
    ? "No registrations yet."
    : categoryRanked.filter(c => c.value > 0).slice(0, 2).map(c =>
        `${c.label} account for ${pctOf(c.value, categoryAgg.total)}% of registrations`
      ).join(", followed by ") + ".";

  const ageRanked = [...ageData].sort((a, b) => b.value - a.value);
  const ageSummary = ageTotal === 0
    ? "No attendees yet."
    : ageRanked.filter(a => a.value > 0).slice(0, 2).map(a =>
        `${a.name} represent ${pctOf(a.value, ageTotal)}% of attendees`
      ).join(", and ") + ".";

  const paymentSummary = paymentAgg.total === 0
    ? "No payment data yet."
    : `${pctOf(paymentAgg.paid_now, paymentAgg.total)}% paid online, ${pctOf(paymentAgg.pay_later, paymentAgg.total)}% selected pay later, and ${pctOf(paymentAgg.in_person, paymentAgg.total)}% will pay in person.`;

  const highlights = [
    { label: "Total Registrations", value: totalRegistrations },
    { label: "Total Attendees", value: totals.total_members },
    { label: "Most Popular Category", value: categoryAgg.total ? categoryRanked[0].label : "—" },
    { label: "Largest Age Group", value: ageTotal ? ageRanked[0].name : "—" },
    { label: "Most Common Payment", value: paymentAgg.total
        ? ([
            { k: "Online Paid", v: paymentAgg.paid_now },
            { k: "Pay Later", v: paymentAgg.pay_later },
            { k: "Pay In Person", v: paymentAgg.in_person },
          ].sort((a, b) => b.v - a.v)[0].k)
        : "—" },
    { label: "Region Leader", value: (!isScoped && topRegion) ? topRegion.name : (scopedRegion || "—") },
  ];

  const lastUpdated = new Date().toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });

  // ---- Export builders ----
  const scopeLabel = isScoped ? (scopedRegion || "Region") : "All Regions";
  const fileBase = `conference-analytics-${year}-${scopeLabel.toLowerCase().replace(/\s+/g, "-")}`;
  const sanitize = (s: string) => s.replace(/[\\\/\?\*\[\]:]/g, "").slice(0, 31);

  const buildSections = () => {
    const overview: (string | number)[][] = [
      ["Conference Year", year],
      ["Scope", scopeLabel],
      ["Generated", lastUpdated],
      ["Total Registrations (Families)", totalRegistrations],
      ["Total Attendees", totals.total_members],
      ["Confirmed", totals.confirmed],
      ["Pending", totals.pending],
      ["Cancelled", cancelledCount],
      ["Rejected", totals.rejected],
      ["Cities Reached", citiesReached],
    ];
    const ageRows = ageData.map(a => [a.name, a.value, `${pctOf(a.value, ageTotal)}%`]);
    const categoryRows = categoryRanked.map(c => [c.label, c.value, `${pctOf(c.value, categoryAgg.total)}%`]);
    const paymentRows = [
      ["Paid Online", paymentAgg.paid_now, `${pctOf(paymentAgg.paid_now, paymentAgg.total)}%`],
      ["Pay Later", paymentAgg.pay_later, `${pctOf(paymentAgg.pay_later, paymentAgg.total)}%`],
      ["Pay In Person", paymentAgg.in_person, `${pctOf(paymentAgg.in_person, paymentAgg.total)}%`],
    ];
    const regionRowsOut = regionRows.map(r => [
      r.region_name, r.families, r.total_members, r.adults, r.teens, r.children_6_12, r.children_0_5,
    ]);
    const cityRowsOut = filteredCityRows.map(c => [
      // Cancellations are tracked per region, not per city — leave city column blank.
      c.region, c.city, c.families, c.total_members, c.confirmed, c.pending, "", c.rejected,
    ]);
    const cancelRowsOut = cancelRows.map(c => [c.region, c.pending, c.approved, c.rejected, c.total]);
    const recentRows = recentRegs.map(r => [
      `${r.first_name} ${r.last_name}`, r.region, r.city || "—", r.total_members, r.payment_status,
      new Date(r.created_at).toLocaleString(),
    ]);
    return [
      { title: "Overview", headers: ["Metric", "Value"], rows: overview },
      { title: "Age Groups", headers: ["Group", "Count", "% of Attendees"], rows: ageRows },
      { title: "Categories", headers: ["Category", "Count", "% of Registrations"], rows: categoryRows },
      { title: "Payment Methods", headers: ["Method", "Count", "%"], rows: paymentRows },
      { title: "By Region", headers: ["Region", "Families", "Attendees", "Adults", "Teens", "Children 6-12", "Children 0-5"], rows: regionRowsOut },
      { title: "By City", headers: ["Region", "City", "Families", "Attendees", "Confirmed", "Pending", "Cancelled", "Rejected"], rows: cityRowsOut },
      { title: "Cancellations", headers: ["Region", "Pending", "Approved", "Rejected", "Total"], rows: cancelRowsOut },
      { title: "Recent Registrations", headers: ["Name", "Region", "City", "Members", "Status", "Created"], rows: recentRows },
    ];
  };

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    buildSections().forEach(s => {
      const ws = XLSX.utils.aoa_to_sheet([s.headers, ...s.rows]);
      XLSX.utils.book_append_sheet(wb, ws, sanitize(s.title));
    });
    XLSX.writeFile(wb, `${fileBase}.xlsx`);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    doc.setFontSize(16);
    doc.text(`Conference Analytics ${year} — ${scopeLabel}`, 40, 40);
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(`Generated ${lastUpdated}`, 40, 58);
    let y = 80;
    buildSections().forEach((s, idx) => {
      if (idx > 0) y += 6;
      doc.setFontSize(12);
      doc.setTextColor(0, 107, 107);
      doc.text(s.title, 40, y);
      y += 6;
      autoTable(doc, {
        head: [s.headers],
        body: s.rows.map(r => r.map(v => String(v ?? ""))),
        startY: y,
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [0, 107, 107] },
        margin: { left: 40, right: 40 },
      });
      y = (doc as any).lastAutoTable.finalY + 16;
      if (y > 720) { doc.addPage(); y = 40; }
    });
    doc.save(`${fileBase}.pdf`);
  };

  const handleExportAISummary = () => {
    const doc = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 48;
    const width = 612 - margin * 2;
    doc.setFontSize(18);
    doc.text(`AI Summary Report — ${year}`, margin, 56);
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(`${scopeLabel} · Generated ${lastUpdated}`, margin, 74);

    const topCat = categoryAgg.total ? categoryRanked[0] : null;
    const topAge = ageTotal ? ageRanked[0] : null;
    const topPay = paymentAgg.total
      ? [
          { k: "online", v: paymentAgg.paid_now },
          { k: "pay later", v: paymentAgg.pay_later },
          { k: "in person", v: paymentAgg.in_person },
        ].sort((a, b) => b.v - a.v)[0]
      : null;
    const conf = pctOf(totals.confirmed, totalRegistrations);
    const pend = pctOf(totals.pending, totalRegistrations);
    const trendTxt = trend ? `Registrations are trending ${trend.dir} ${trend.pct}% vs. the prior 7-day window.` : "";

    const paragraphs = [
      `Executive Overview`,
      execSummary,
      `Performance`,
      `${totals.confirmed} of ${totalRegistrations} registrations are confirmed (${conf}%), with ${totals.pending} pending (${pend}%), ${cancelledCount} cancelled, and ${totals.rejected} rejected. ${trendTxt}`,
      `Audience Composition`,
      `${topCat ? `${topCat.label} make up the largest registration category (${pctOf(topCat.value, categoryAgg.total)}%).` : "No category data yet."} ${topAge ? `The largest age cohort is ${topAge.name} (${pctOf(topAge.value, ageTotal)}% of attendees).` : ""}`,
      `Payment Behaviour`,
      topPay ? `${pctOf(paymentAgg.paid_now, paymentAgg.total)}% paid online, ${pctOf(paymentAgg.pay_later, paymentAgg.total)}% chose pay later, and ${pctOf(paymentAgg.in_person, paymentAgg.total)}% will pay in person. The most common channel is ${topPay.k}.` : "No payment data yet.",
      `Geographic Reach`,
      `Registrations span ${citiesReached} ${citiesReached === 1 ? "city" : "cities"}.${(!isScoped && topRegion) ? ` ${topRegion.name} Region leads with ${topRegion.pct}% of attendees.` : ""}`,
      `Recommendations`,
      `${pend > 30 ? "Follow up on pending payments to lift the confirmation rate. " : ""}${topPay?.k === "pay later" ? "Send payment reminders to attendees who selected pay later. " : ""}${trend?.dir === "down" ? "Boost outreach — recent registration momentum is slowing. " : "Maintain current outreach cadence — momentum is positive. "}`,
    ];

    let y = 100;
    doc.setTextColor(20);
    paragraphs.forEach((p, i) => {
      const isHeading = i % 2 === 0;
      doc.setFontSize(isHeading ? 12 : 10);
      doc.setFont(undefined as any, isHeading ? "bold" : "normal");
      const lines = doc.splitTextToSize(p, width);
      if (y + lines.length * (isHeading ? 16 : 14) > 740) { doc.addPage(); y = 60; }
      doc.text(lines, margin, y);
      y += lines.length * (isHeading ? 16 : 14) + (isHeading ? 4 : 10);
    });

    doc.save(`${fileBase}-ai-summary.pdf`);
  };


  return (
    <div className="space-y-5">
      {/* Top toolbar: year / region / date / export */}
      <Card>
        <CardContent className="p-3 flex flex-wrap items-center gap-2 justify-end">
          <div className="flex items-center gap-2 mr-auto text-xs text-muted-foreground">
            <Info className="w-4 h-4" />
            <span>Real-time overview of registrations and attendance.</span>
          </div>
          <div className="flex items-center gap-1.5 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs">
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="font-medium">{year}{status === "current" ? " (Current)" : ""}</span>
          </div>
          {(() => {
            const isGlobalViewer = adminRole === "global_admin" || adminRole === "president";
            if (isGlobalViewer && !isScoped) {
              return (
                <Select value={regionFilter} onValueChange={setRegionFilter}>
                  <SelectTrigger className="h-8 w-44 text-xs">
                    <MapPin className="w-3.5 h-3.5 mr-1 text-muted-foreground" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Regions</SelectItem>
                    {allRegionNames.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              );
            }
            return (
              <div className="flex items-center gap-1.5 rounded-md border bg-muted/40 px-2.5 py-1.5 text-xs">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="font-medium">{scopedRegion || "—"}</span>
              </div>
            );
          })()}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 w-40 text-xs">
              <Filter className="w-3.5 h-3.5 mr-1 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 text-xs">
                <Download className="w-3.5 h-3.5 mr-1" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Download dashboard</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleExportExcel}>
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" /> Excel workbook (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPDF}>
                <FileType className="w-4 h-4 mr-2 text-rose-600" /> Full PDF report
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleExportAISummary}>
                <Sparkles className="w-4 h-4 mr-2 text-amber-500" /> AI Summary report (PDF)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardContent>
      </Card>

      {/* Scope banner */}
      {isScoped && (
        <Card className="border-sky-200 bg-sky-50/60">
          <CardContent className="p-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-sky-700" />
              </div>
              <div className="text-sm">
                <div className="font-medium text-sky-900">
                  You are viewing analytics for: <span className="text-sky-700">{scopedRegion}</span>
                </div>
                <div className="text-xs text-sky-800/80">
                  As a Regional Admin, you can only see data for your assigned region.
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Data Synced
              </Badge>
              <span className="text-muted-foreground">Just now</span>
              <RefreshCw className="w-3.5 h-3.5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Executive Summary */}
      <Card className="border-l-4 border-l-primary">
        <CardContent className="p-5 flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-3 md:flex-1">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <BarChart3 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="text-sm font-semibold">Executive Summary</div>
              <div className="text-sm text-muted-foreground leading-snug">{execSummary}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {reconciles ? (
              <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                <ShieldCheck className="w-3 h-3 mr-1" /> Totals reconcile
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-amber-50 text-amber-800 border border-amber-200" title={`Sum of statuses (${totalStatus}) ≠ total registrations (${totalRegistrations}).`}>
                <AlertTriangle className="w-3 h-3 mr-1" /> Syncing… {totalStatus}/{totalRegistrations}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Conference Highlights — key insights */}
      <Card className="bg-gradient-to-br from-primary/5 via-background to-background">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-500" /> Conference Highlights
            <span className="text-xs font-normal text-muted-foreground">Key insights at a glance</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {highlights.map(h => (
              <div key={h.label} className="rounded-lg border bg-card p-3">
                <div className="text-[11px] font-medium text-muted-foreground leading-tight">{h.label}</div>
                <div className="text-xl font-bold mt-1 leading-tight break-words">{h.value}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {kpis.filter(k => k.key !== "attendees").map(k => (
          <KpiCard key={k.key} kpi={k} trend={trend} />
        ))}
      </div>

      {/* Row: Funnel | Regions bar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" /> Registration Funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FunnelSteps
              steps={[
                { label: "Registered", value: totalRegistrations, color: "sky", icon: Users },
                { label: "Pending", value: totals.pending, color: "amber", icon: Clock },
                { label: "Confirmed", value: totals.confirmed, color: "emerald", icon: CheckCircle2 },
                { label: "Attended", value: confirmedAttendees(filteredCityRows), color: "violet", icon: User },
              ]}
            />
            <div className="mt-4 flex items-center justify-center gap-2 text-xs">
              <span className="text-muted-foreground">Conversion Rate (Confirmed ÷ Registered):</span>
              <Badge variant="secondary" className="bg-sky-50 text-sky-700 border border-sky-200">
                {totalRegistrations ? ((totals.confirmed / totalRegistrations) * 100).toFixed(1) : "0.0"}%
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary" /> Registrations by Region
            </CardTitle>
            <Badge variant="outline" className="text-[10px] h-5">Bar</Badge>
          </CardHeader>
          <CardContent>
            <RegionMiniBar regionRows={regionRows} cityRows={cityRows} isScoped={isScoped} scopedRegion={scopedRegion} />
          </CardContent>
        </Card>
      </div>

      {/* Registration Category Metrics — full width */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ListChecks className="w-4 h-4 text-primary" /> Registration Category Metrics
          </CardTitle>
          {categoryAgg.total > 0 && (
            <p className="text-sm text-muted-foreground mt-1">{categorySummary}</p>
          )}
        </CardHeader>
        <CardContent className="space-y-5">
          {categoryAgg.total === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No registrations yet.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { key: "individual", label: "Individuals", value: categoryAgg.individual, icon: User, color: "#0EA5E9", bg: "bg-sky-50", text: "text-sky-700", border: "border-sky-200" },
                  { key: "student",    label: "Students",    value: categoryAgg.student,    icon: GraduationCap, color: "#8B5CF6", bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200" },
                  { key: "couple",     label: "Couples",     value: categoryAgg.couple,     icon: Heart, color: "#EC4899", bg: "bg-pink-50", text: "text-pink-700", border: "border-pink-200" },
                  { key: "family",     label: "Families",    value: categoryAgg.family,     icon: UsersRound, color: "#F59E0B", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
                ].map(c => {
                  const pct = categoryAgg.total ? (c.value / categoryAgg.total) * 100 : 0;
                  const Icon = c.icon;
                  return (
                    <div key={c.key} className={`rounded-xl border ${c.border} ${c.bg} p-4`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className={`text-sm font-semibold ${c.text}`}>{c.label}</div>
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${c.text}`} style={{ background: `${c.color}1A` }}>
                          <Icon className="w-4 h-4" />
                        </div>
                      </div>
                      <div className="text-3xl font-extrabold text-foreground leading-none">{c.value}</div>
                      <div className="text-xs text-muted-foreground mt-1">{c.value === 1 ? "Registration" : "Registrations"}</div>
                      <div className="mt-3 h-1.5 bg-white/60 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: c.color }} />
                      </div>
                      <div className={`text-sm font-semibold mt-1 ${c.text}`}>{pct.toFixed(0)}%</div>
                    </div>
                  );
                })}
              </div>
              <div>
                <div className="text-xs font-medium mb-1 text-muted-foreground">Registrations by Category</div>
                <div style={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        { name: "Individuals", value: categoryAgg.individual, fill: "#0EA5E9" },
                        { name: "Students", value: categoryAgg.student, fill: "#8B5CF6" },
                        { name: "Couples", value: categoryAgg.couple, fill: "#EC4899" },
                        { name: "Families", value: categoryAgg.family, fill: "#F59E0B" },
                      ]}
                      margin={{ top: 22, right: 12, left: 0, bottom: 4 }}
                      barCategoryGap="35%"
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={28} />
                      <Tooltip cursor={{ fill: "hsl(var(--muted))", opacity: 0.3 }} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={56} label={{ position: "top", fontSize: 11, fontWeight: 600 }}>
                        {["#0EA5E9", "#8B5CF6", "#EC4899", "#F59E0B"].map((c, i) => <Cell key={i} fill={c} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center justify-between text-xs mt-1">
                  <span className="text-muted-foreground">
                    Total ({categoryAgg.individual} + {categoryAgg.student} + {categoryAgg.couple} + {categoryAgg.family}) = {categoryAgg.total}
                  </span>
                  <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] h-5">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Matches total
                  </Badge>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Age Metrics — full width */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" /> Age Metrics
            <span className="text-xs font-normal text-muted-foreground">(Attendees)</span>
          </CardTitle>
          {ageTotal > 0 && (
            <p className="text-sm text-muted-foreground mt-1">{ageSummary}</p>
          )}
        </CardHeader>
        <CardContent>
          {ageTotal === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No attendees yet.</p>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {ageData.map((a, i) => (
                  <MetricDonut
                    key={a.name}
                    label={a.name}
                    value={a.value}
                    total={ageTotal}
                    color={["#1E3A8A", "#60A5FA", "#F59E0B", "#10B981"][i]}
                    unit={a.value === 1 ? "Attendee" : "Attendees"}
                  />
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between text-xs border-t pt-3">
                <span className="text-muted-foreground">Total Attendees: <span className="font-semibold text-foreground">{ageTotal}</span></span>
                <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] h-5">
                  100% Matches confirmed + pending + cancelled
                </Badge>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Payment Method — full width */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-primary" /> Payment Method Breakdown
            </CardTitle>
            {paymentAgg.total > 0 && (
              <p className="text-sm text-muted-foreground mt-1">{paymentSummary}</p>
            )}
          </div>
          <Badge variant="outline" className="text-[10px] h-5">Bar</Badge>
        </CardHeader>
        <CardContent className="space-y-4">
          {paymentAgg.total === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No registrations yet.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { label: "Online Paid", value: paymentAgg.paid_now, color: "#1E3A8A", bg: "bg-sky-50", text: "text-sky-700", border: "border-sky-200", icon: CreditCard },
                  { label: "Pay Later", value: paymentAgg.pay_later, color: "#F59E0B", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", icon: Wallet },
                  { label: "Pay In Person", value: paymentAgg.in_person, color: "#10B981", bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", icon: HandCoins },
                ].map(p => {
                  const pct = paymentAgg.total ? (p.value / paymentAgg.total) * 100 : 0;
                  const Icon = p.icon;
                  return (
                    <div key={p.label} className={`rounded-xl border ${p.border} ${p.bg} p-4`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className={`text-sm font-semibold ${p.text}`}>{p.label}</div>
                        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${p.text}`} style={{ background: `${p.color}1A` }}>
                          <Icon className="w-4 h-4" />
                        </div>
                      </div>
                      <div className="text-3xl font-extrabold text-foreground leading-none">{p.value}</div>
                      <div className="text-xs text-muted-foreground mt-1">{p.value === 1 ? "Registration" : "Registrations"}</div>
                      <div className="mt-3 h-1.5 bg-white/60 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: p.color }} />
                      </div>
                      <div className={`text-sm font-semibold mt-1 ${p.text}`}>{pct.toFixed(0)}%</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { name: "Online Paid", value: paymentAgg.paid_now, fill: "#1E3A8A" },
                      { name: "Pay Later", value: paymentAgg.pay_later, fill: "#F59E0B" },
                      { name: "Pay In Person", value: paymentAgg.in_person, fill: "#10B981" },
                    ]}
                    margin={{ top: 22, right: 12, left: 0, bottom: 4 }}
                    barCategoryGap="35%"
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={28} />
                    <Tooltip cursor={{ fill: "hsl(var(--muted))", opacity: 0.3 }} />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={56} label={{ position: "top", fontSize: 11, fontWeight: 600, fill: "hsl(var(--foreground))" }}>
                      {["#1E3A8A", "#F59E0B", "#10B981"].map((c, i) => <Cell key={i} fill={c} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-between text-[11px] pt-1">
                <span className="text-muted-foreground">Total payment related: {paymentAgg.tracked}</span>
                {paymentAgg.tracked === paymentAgg.total ? (
                  <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] h-5">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Matches total
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px] h-5">
                    <AlertTriangle className="w-3 h-3 mr-1" /> {paymentAgg.tracked}/{paymentAgg.total}
                  </Badge>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>


      {/* Regional Analytics table — kept for global admins */}
      {!isScoped && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>Regional Analytics</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Compare registration performance across regions.</p>
            </div>
            <Tabs value={regionView} onValueChange={(v) => setRegionView(v as any)}>
              <TabsList className="h-8">
                <TabsTrigger value="bar" className="text-xs h-6">Bar chart</TabsTrigger>
                <TabsTrigger value="table" className="text-xs h-6">Table</TabsTrigger>
                <TabsTrigger value="percent" className="text-xs h-6">Percentage</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            <RegionalAnalytics
              regionRows={regionRows}
              cityRows={cityRows}
              cancelRows={cancelRows}
              view={regionView}
            />
          </CardContent>
        </Card>
      )}

      {/* Recent Activity — moved to bottom of executive-priority order */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="w-4 h-4 text-primary" /> Recent Activity
          </CardTitle>
          <Button variant="ghost" size="sm" className="text-xs h-6 px-2 text-primary">View All</Button>
        </CardHeader>
        <CardContent className="pt-0">
          <RecentActivityCompact regs={recentRegs} cancels={recentCancels} statusFilter={statusFilter} />
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground border-t pt-3">
        <span>All data is real-time and synchronized with the registration system.</span>
        <span className="flex items-center gap-1">
          Last updated: <span className="font-medium text-foreground">{lastUpdated}</span>
          <RefreshCw className="w-3 h-3 ml-1" />
        </span>
      </div>

      {/* Registrations over time */}
      {status === "current" && (
        <Card>
          <CardHeader><CardTitle>Registrations Over Time (last 60 days)</CardTitle></CardHeader>
          <CardContent style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={dailyRows.map(d => ({ ...d, day: new Date(d.day).toLocaleDateString(undefined, { month: "short", day: "numeric" }) }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="registrations" stroke="hsl(var(--primary))" strokeWidth={2} />
                <Line type="monotone" dataKey="members" stroke="#F59E0B" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Cancellation breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ban className="w-5 h-5 text-rose-600" /> Cancellation Requests {isScoped ? `— ${scopedRegion}` : "by Region"}
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {cancelRows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No cancellation requests yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Region</TableHead>
                  <TableHead className="text-right">Pending</TableHead>
                  <TableHead className="text-right">Approved</TableHead>
                  <TableHead className="text-right">Rejected</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cancelRows.map((c, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{c.region}</TableCell>
                    <TableCell className="text-right text-amber-600">{c.pending}</TableCell>
                    <TableCell className="text-right text-rose-600">{c.approved}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{c.rejected}</TableCell>
                    <TableCell className="text-right font-semibold">{c.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Feedback aggregates */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" /> Feedback Summary {isScoped ? `— ${scopedRegion}` : "by Region"}
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            disabled={feedbackRows.length === 0}
            onClick={() => downloadFeedbackCsv(feedbackRows, slug)}
          >
            <Download className="w-4 h-4 mr-2" /> Download CSV
          </Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {feedbackRows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No feedback submitted yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Region</TableHead>
                  <TableHead className="text-right">Responses</TableHead>
                  <TableHead className="text-right">Food</TableHead>
                  <TableHead className="text-right">Venue</TableHead>
                  <TableHead className="text-right">Speakers</TableHead>
                  <TableHead className="text-right">Timekeeping</TableHead>
                  <TableHead className="text-right">Program</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feedbackRows.map((f, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{f.region}</TableCell>
                    <TableCell className="text-right">{f.total}</TableCell>
                    <TableCell className="text-right">{f.avg_food ?? "—"}</TableCell>
                    <TableCell className="text-right">{f.avg_venue ?? "—"}</TableCell>
                    <TableCell className="text-right">{f.avg_speakers ?? "—"}</TableCell>
                    <TableCell className="text-right">{f.avg_timekeeping ?? "—"}</TableCell>
                    <TableCell className="text-right">{f.avg_program ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detailed feedback responses */}
      <FeedbackDetailsCard rows={feedbackDetails} slug={slug} />

      {/* Registration Sources — hidden for scoped regional admins (source RPC is global-only) */}

      {!isScoped && <SourceBreakdownCard rows={sourceRows} />}

      {/* City hub breakdown (Southern Region) */}
      {(isScoped ? /southern/i.test(scopedRegion || "") : (regionFilter === "all" || /southern/i.test(regionFilter))) && (
        <CityHubBreakdown rows={filteredCityRows.filter(r => /southern/i.test(r.region))} />
      )}

      {/* City breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>City-wise Breakdown</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {filteredCityRows.length} {filteredCityRows.length === 1 ? "city" : "cities"} shown
            {!isScoped && regionFilter !== "all" ? ` for ${regionFilter}` : ""}.
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Region</TableHead>
                <TableHead>City</TableHead>
                <TableHead className="text-right">Families</TableHead>
                <TableHead className="text-right">Members</TableHead>
                <TableHead className="text-right">Adults</TableHead>
                <TableHead className="text-right">Teens</TableHead>
                <TableHead className="text-right">6–12</TableHead>
                <TableHead className="text-right">0–5</TableHead>
                <TableHead className="text-right">Confirmed</TableHead>
                <TableHead className="text-right">Pending</TableHead>
                <TableHead className="text-right">Cancelled</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCityRows.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-6">No registrations yet.</TableCell></TableRow>
              ) : filteredCityRows.map((c, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{c.region}</TableCell>
                  <TableCell>{c.city}</TableCell>
                  <TableCell className="text-right">{c.families}</TableCell>
                  <TableCell className="text-right">{c.total_members}</TableCell>
                  <TableCell className="text-right">{c.adults}</TableCell>
                  <TableCell className="text-right">{c.teens}</TableCell>
                  <TableCell className="text-right">{c.children_6_12}</TableCell>
                  <TableCell className="text-right">{c.children_0_5}</TableCell>
                  <TableCell className="text-right text-emerald-600">{c.confirmed}</TableCell>
                  <TableCell className="text-right text-amber-600">{c.pending}</TableCell>
                  <TableCell className="text-right text-muted-foreground">—</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function confirmedAttendees(cityRows: CityRow[]) {
  // Approximate: members in confirmed families. We don't have a direct breakdown,
  // so we scale total_members by confirmed share per city.
  return cityRows.reduce((sum, c) => {
    const fam = Number(c.families || 0);
    const conf = Number(c.confirmed || 0);
    const mem = Number(c.total_members || 0);
    return sum + (fam > 0 ? Math.round((conf / fam) * mem) : 0);
  }, 0);
}

// Southern Region city-hub mapping. Cities not listed fall into "Other".
const SOUTHERN_CITY_HUBS: { hub: string; match: RegExp }[] = [
  { hub: "Dallas – DFW Metroplex", match: /\b(dallas|fort worth|ft\.? worth|plano|frisco|irving|arlington|richardson|mckinney|allen|carrollton|denton|grapevine|lewisville|garland|mesquite|rockwall|coppell|flower mound|euless|bedford|hurst|southlake|keller|prosper|little elm|the colony|addison|farmers branch|desoto|duncanville|cedar hill|mansfield|wylie|murphy|sachse|anna|celina|melissa|princeton|forney|midlothian|waxahachie)\b/i },
  { hub: "Houston – Greater Houston", match: /\b(houston|katy|sugar\s*land|pearland|spring|the woodlands|woodlands|cypress|humble|missouri city|league city|conroe|kingwood|tomball|richmond|rosenberg|stafford|fulshear|friendswood|dickinson|texas city|baytown|deer park|pasadena|channelview|atascocita|manvel)\b/i },
  { hub: "Austin – Greater Austin", match: /\b(austin|round rock|cedar park|pflugerville|georgetown|leander|buda|kyle|hutto|manor|lakeway|bee cave|dripping springs|liberty hill|taylor|elgin|del valle)\b/i },
  { hub: "San Antonio – Greater San Antonio", match: /\b(san antonio|new braunfels|schertz|cibolo|boerne|converse|selma|helotes|live oak|universal city|leon valley|windcrest|kirby|seguin|floresville|san marcos)\b/i },
];

function cityHubFor(city: string | null | undefined): string {
  const c = (city || "").trim();
  if (!c) return "Other / Unspecified";
  for (const h of SOUTHERN_CITY_HUBS) if (h.match.test(c)) return h.hub;
  return `Other – ${c}`;
}

function CityHubBreakdown({ rows }: { rows: CityRow[] }) {
  const hubs = useMemo(() => {
    const map = new Map<string, { hub: string; cities: Set<string>; families: number; total_members: number; adults: number; teens: number; children_6_12: number; children_0_5: number; confirmed: number; pending: number }>();
    rows.forEach(r => {
      const hub = cityHubFor(r.city);
      const cur = map.get(hub) || { hub, cities: new Set(), families: 0, total_members: 0, adults: 0, teens: 0, children_6_12: 0, children_0_5: 0, confirmed: 0, pending: 0 };
      if (r.city) cur.cities.add(r.city);
      cur.families += Number(r.families || 0);
      cur.total_members += Number(r.total_members || 0);
      cur.adults += Number(r.adults || 0);
      cur.teens += Number(r.teens || 0);
      cur.children_6_12 += Number(r.children_6_12 || 0);
      cur.children_0_5 += Number(r.children_0_5 || 0);
      cur.confirmed += Number(r.confirmed || 0);
      cur.pending += Number(r.pending || 0);
      map.set(hub, cur);
    });
    return Array.from(map.values()).sort((a, b) => b.families - a.families);
  }, [rows]);

  const totalFamilies = hubs.reduce((s, h) => s + h.families, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>City Hub Breakdown — Southern Region</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Registrations grouped by major metro area (Dallas/DFW, Houston, Austin, San Antonio, Other).
        </p>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>City Hub</TableHead>
              <TableHead className="text-right">Cities</TableHead>
              <TableHead className="text-right">Families</TableHead>
              <TableHead className="text-right">Share</TableHead>
              <TableHead className="text-right">Members</TableHead>
              <TableHead className="text-right">Adults</TableHead>
              <TableHead className="text-right">Teens</TableHead>
              <TableHead className="text-right">6–12</TableHead>
              <TableHead className="text-right">0–5</TableHead>
              <TableHead className="text-right">Confirmed</TableHead>
              <TableHead className="text-right">Pending</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {hubs.length === 0 ? (
              <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-6">No registrations yet.</TableCell></TableRow>
            ) : hubs.map(h => (
              <TableRow key={h.hub}>
                <TableCell className="font-medium">{h.hub}</TableCell>
                <TableCell className="text-right">{h.cities.size}</TableCell>
                <TableCell className="text-right font-semibold">{h.families}</TableCell>
                <TableCell className="text-right text-muted-foreground">{totalFamilies > 0 ? `${Math.round((h.families / totalFamilies) * 100)}%` : "—"}</TableCell>
                <TableCell className="text-right">{h.total_members}</TableCell>
                <TableCell className="text-right">{h.adults}</TableCell>
                <TableCell className="text-right">{h.teens}</TableCell>
                <TableCell className="text-right">{h.children_6_12}</TableCell>
                <TableCell className="text-right">{h.children_0_5}</TableCell>
                <TableCell className="text-right text-emerald-600">{h.confirmed}</TableCell>
                <TableCell className="text-right text-amber-600">{h.pending}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}


function Funnel({ steps }: { steps: { label: string; value: number; color: string }[] }) {
  const max = Math.max(1, ...steps.map(s => s.value));
  return (
    <div className="space-y-2">
      {steps.map((s, i) => {
        const pct = (s.value / max) * 100;
        const prev = i > 0 ? steps[i - 1].value : null;
        const conv = prev && prev > 0 ? Math.round((s.value / prev) * 100) : null;
        return (
          <div key={s.label}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-medium">{s.label}</span>
              <span className="text-muted-foreground">
                {s.value.toLocaleString()}
                {conv !== null && <span className="ml-2">· {conv}% of {steps[i - 1].label.toLowerCase()}</span>}
              </span>
            </div>
            <div className="h-7 bg-muted rounded-md overflow-hidden">
              <div className={`h-full ${s.color} flex items-center justify-end px-2 text-[11px] font-medium text-white transition-all`}
                   style={{ width: `${Math.max(pct, 4)}%` }}>
                {pct > 8 ? `${Math.round(pct)}%` : ""}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RegionalAnalytics({
  regionRows, cityRows, cancelRows, view,
}: { regionRows: RegionRow[]; cityRows: CityRow[]; cancelRows: CancelRow[]; view: "bar" | "table" | "percent" }) {
  const merged = useMemo(() => {
    const byRegion: Record<string, {
      region: string; registrations: number; confirmed: number; pending: number;
      cancelled: number; rejected: number; cities: Set<string>; members: number;
    }> = {};
    cityRows.forEach(c => {
      const key = c.region;
      if (!byRegion[key]) byRegion[key] = { region: key, registrations: 0, confirmed: 0, pending: 0, cancelled: 0, rejected: 0, cities: new Set(), members: 0 };
      byRegion[key].registrations += Number(c.families || 0);
      byRegion[key].confirmed += Number(c.confirmed || 0);
      byRegion[key].pending += Number(c.pending || 0);
      // NOTE: c.cancelled is region-wide (repeated per city) — don't sum. Filled from cancelRows below.
      byRegion[key].rejected += Number(c.rejected || 0);
      byRegion[key].members += Number(c.total_members || 0);
      if (c.city) byRegion[key].cities.add(c.city);
    });
    regionRows.forEach(r => {
      if (!byRegion[r.region_name]) byRegion[r.region_name] = { region: r.region_name, registrations: Number(r.families || 0), confirmed: 0, pending: 0, cancelled: 0, rejected: 0, cities: new Set(), members: Number(r.total_members || 0) };
    });
    // Fill cancelled from the trustworthy per-region admin_cancellation_counts source.
    cancelRows.forEach(cr => {
      if (!byRegion[cr.region]) byRegion[cr.region] = { region: cr.region, registrations: 0, confirmed: 0, pending: 0, cancelled: 0, rejected: 0, cities: new Set(), members: 0 };
      byRegion[cr.region].cancelled = Number(cr.approved || 0);
    });
    return Object.values(byRegion).sort((a, b) => b.registrations - a.registrations);
  }, [regionRows, cityRows, cancelRows]);

  const totalReg = merged.reduce((s, r) => s + r.registrations, 0) || 1;

  if (merged.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">No regional data yet.</p>;
  }

  if (view === "bar") {
    const data = merged.map(r => ({
      name: r.region.replace(" Region", ""),
      Registrations: r.registrations,
      Confirmed: r.confirmed,
      Pending: r.pending,
      Cities: r.cities.size,
    }));
    return (
      <div style={{ height: 340 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Bar dataKey="Registrations" fill="hsl(var(--primary))" />
            <Bar dataKey="Confirmed" fill="#10B981" />
            <Bar dataKey="Pending" fill="#F59E0B" />
            <Bar dataKey="Cities" fill="#8B5CF6" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (view === "percent") {
    return (
      <div className="space-y-3">
        {merged.map(r => {
          const pct = (r.registrations / totalReg) * 100;
          return (
            <div key={r.region}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="font-medium">{r.region}</span>
                <span className="text-muted-foreground">{r.registrations} · {pct.toFixed(1)}%</span>
              </div>
              <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Region</TableHead>
            <TableHead className="text-right">Registrations</TableHead>
            <TableHead className="text-right">Confirmed</TableHead>
            <TableHead className="text-right">Pending</TableHead>
            <TableHead className="text-right">Cities</TableHead>
            <TableHead className="text-right">Attendees</TableHead>
            <TableHead className="text-right">Share</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {merged.map(r => (
            <TableRow key={r.region}>
              <TableCell className="font-medium">{r.region}</TableCell>
              <TableCell className="text-right">{r.registrations}</TableCell>
              <TableCell className="text-right text-emerald-600">{r.confirmed}</TableCell>
              <TableCell className="text-right text-amber-600">{r.pending}</TableCell>
              <TableCell className="text-right">{r.cities.size}</TableCell>
              <TableCell className="text-right">{r.members}</TableCell>
              <TableCell className="text-right text-muted-foreground">
                {((r.registrations / totalReg) * 100).toFixed(1)}%
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function RecentActivity({
  regs, cancels, statusFilter,
}: { regs: RecentRegRow[]; cancels: RecentCancelRow[]; statusFilter: string }) {
  type Item = { kind: "registered" | "confirmed" | "cancelled"; ts: string; label: string; sub: string };
  const items: Item[] = [];
  regs.forEach(r => {
    if (statusFilter !== "all" && statusFilter !== r.payment_status) return;
    items.push({
      kind: "registered",
      ts: r.created_at,
      label: `${r.first_name} ${r.last_name}`,
      sub: `${r.city ? r.city + ", " : ""}${r.region} · ${r.total_members} ${r.total_members === 1 ? "person" : "people"}`,
    });
    if (r.payment_status === "confirmed" && r.updated_at && r.updated_at !== r.created_at) {
      items.push({
        kind: "confirmed",
        ts: r.updated_at,
        label: `Confirmed: ${r.first_name} ${r.last_name}`,
        sub: `${r.city ? r.city + ", " : ""}${r.region}`,
      });
    }
  });
  cancels.forEach(c => {
    if (statusFilter !== "all" && statusFilter !== "cancelled") return;
    items.push({
      kind: "cancelled",
      ts: c.created_at,
      label: `Cancellation request (${c.status})`,
      sub: c.region,
    });
  });
  items.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
  const top = items.slice(0, 12);

  if (top.length === 0) {
    return <p className="text-sm text-muted-foreground py-4">No recent activity.</p>;
  }

  const iconFor = (k: Item["kind"]) =>
    k === "confirmed" ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> :
    k === "cancelled" ? <Ban className="w-4 h-4 text-rose-600" /> :
                        <ListChecks className="w-4 h-4 text-primary" />;

  return (
    <ul className="space-y-3 max-h-[320px] overflow-y-auto pr-1">
      {top.map((it, i) => (
        <li key={i} className="flex items-start gap-3 text-sm">
          <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
            {iconFor(it.kind)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-medium truncate">{it.label}</div>
            <div className="text-xs text-muted-foreground truncate">{it.sub}</div>
          </div>
          <div className="text-xs text-muted-foreground shrink-0">{timeAgo(it.ts)}</div>
        </li>
      ))}
    </ul>
  );
}

function timeAgo(iso: string) {
  const d = new Date(iso).getTime();
  const diff = Date.now() - d;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const FEEDBACK_TOPICS = [
  { key: "food", label: "Food" },
  { key: "venue", label: "Venue" },
  { key: "speakers", label: "Speakers" },
  { key: "timekeeping", label: "Timekeeping" },
  { key: "program", label: "Program" },
  { key: "registration_process", label: "Registration" },
] as const;

function FeedbackDetailsCard({ rows, slug }: { rows: FeedbackDetailRow[]; slug: string }) {
  const [onlyComments, setOnlyComments] = useState(false);

  const hasComment = (r: FeedbackDetailRow) =>
    FEEDBACK_TOPICS.some(t => String((r as any)[`feedback_${t.key}`] || "").trim()) ||
    Boolean(String(r.suggestions || "").trim());

  const visible = onlyComments ? rows.filter(hasComment) : rows;
  const commentCount = rows.filter(hasComment).length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" /> Detailed Feedback &amp; Comments
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {rows.length} {rows.length === 1 ? "response" : "responses"} · {commentCount} with written comments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant={onlyComments ? "default" : "outline"} onClick={() => setOnlyComments(v => !v)}>
            Comments only
          </Button>
          <Button size="sm" variant="outline" disabled={rows.length === 0} onClick={() => downloadFeedbackDetailCsv(rows, slug)}>
            <Download className="w-4 h-4 mr-2" /> Download CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {visible.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No feedback responses to show yet.</p>
        ) : (
          <div className="space-y-3">
            {visible.map(r => {
              const name = [r.first_name, r.last_name].filter(Boolean).join(" ").trim();
              return (
                <div key={r.id} className="rounded-lg border p-4 space-y-3">
                  <div className="flex flex-wrap items-center gap-2 justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{name || "Anonymous"}</span>
                      {r.region && <Badge variant="secondary">{r.region}</Badge>}
                      {(r.city || r.state) && (
                        <span className="text-xs text-muted-foreground">
                          {[r.city, r.state].filter(Boolean).join(", ")}
                        </span>
                      )}
                      {r.attended && <Badge variant="outline" className="text-[10px]">{r.attended}</Badge>}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {FEEDBACK_TOPICS.map(t => {
                      const rating = (r as any)[`rating_${t.key}`] as number | null;
                      if (rating == null) return null;
                      return (
                        <div key={t.key} className="flex items-center gap-1 text-xs">
                          <span className="text-muted-foreground">{t.label}</span>
                          <span className="font-semibold">{rating}</span>
                          <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                        </div>
                      );
                    })}
                  </div>

                  {FEEDBACK_TOPICS.map(t => {
                    const text = String((r as any)[`feedback_${t.key}`] || "").trim();
                    if (!text) return null;
                    return (
                      <p key={t.key} className="text-sm">
                        <span className="text-muted-foreground">{t.label}: </span>{text}
                      </p>
                    );
                  })}

                  {String(r.suggestions || "").trim() && (
                    <p className="text-sm rounded-md bg-muted/50 p-3">
                      <span className="text-muted-foreground">General comments / suggestions: </span>
                      {r.suggestions}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function downloadFeedbackDetailCsv(rows: FeedbackDetailRow[], slug: string) {
  const headers = [
    "Submitted", "Region", "State", "City", "Name", "Email", "Attended",
    ...FEEDBACK_TOPICS.map(t => `${t.label} Rating`),
    ...FEEDBACK_TOPICS.map(t => `${t.label} Comment`),
    "General Comments",
  ];
  const q = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [
    headers.join(","),
    ...rows.map(r => [
      q(new Date(r.created_at).toLocaleString()),
      q(r.region), q(r.state), q(r.city),
      q([r.first_name, r.last_name].filter(Boolean).join(" ")),
      q(r.email), q(r.attended),
      ...FEEDBACK_TOPICS.map(t => q((r as any)[`rating_${t.key}`] ?? "")),
      ...FEEDBACK_TOPICS.map(t => q((r as any)[`feedback_${t.key}`] ?? "")),
      q(r.suggestions),
    ].join(",")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, `feedback-detailed-${slug}-${new Date().toISOString().slice(0, 10)}.csv`);
}


function downloadFeedbackCsv(rows: FeedbackRow[], slug: string) {
  const headers = ["Region", "Responses", "Avg Food", "Avg Venue", "Avg Speakers", "Avg Timekeeping", "Avg Program"];
  const overall = rows.reduce(
    (a, r) => {
      const n = Number(r.total || 0);
      a.total += n;
      a.food += Number(r.avg_food || 0) * n;
      a.venue += Number(r.avg_venue || 0) * n;
      a.speakers += Number(r.avg_speakers || 0) * n;
      a.tk += Number(r.avg_timekeeping || 0) * n;
      a.program += Number(r.avg_program || 0) * n;
      return a;
    },
    { total: 0, food: 0, venue: 0, speakers: 0, tk: 0, program: 0 }
  );
  const avg = (v: number) => (overall.total ? (v / overall.total).toFixed(2) : "—");
  const lines = [
    headers.join(","),
    ...rows.map(r => [
      `"${r.region}"`,
      r.total,
      r.avg_food ?? "",
      r.avg_venue ?? "",
      r.avg_speakers ?? "",
      r.avg_timekeeping ?? "",
      r.avg_program ?? "",
    ].join(",")),
    ["\"OVERALL\"", overall.total, avg(overall.food), avg(overall.venue), avg(overall.speakers), avg(overall.tk), avg(overall.program)].join(","),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `feedback-summary-${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default ConferenceAnalyticsDashboard;

const SOURCE_COLORS: Record<string, string> = {
  Flyer: "hsl(var(--primary))",
  Social: "#8B5CF6",
  Website: "#10B981",
  Email: "#F59E0B",
  Direct: "#64748B",
  Other: "#94A3B8",
};

function SourceBreakdownCard({ rows }: { rows: SourceRow[] }) {
  const buckets = rows.reduce<Record<string, { families: number; members: number }>>((acc, r) => {
    const key = r.source_bucket || "Other";
    if (!acc[key]) acc[key] = { families: 0, members: 0 };
    acc[key].families += Number(r.families || 0);
    acc[key].members += Number(r.total_members || 0);
    return acc;
  }, {});
  const bucketEntries = Object.entries(buckets).sort((a, b) => b[1].families - a[1].families);
  const totalFamilies = bucketEntries.reduce((s, [, v]) => s + v.families, 0);

  const downloadCsv = () => {
    const headers = ["Source Bucket", "Registration Source", "UTM Source", "UTM Medium", "UTM Campaign", "Families", "Members"];
    const lines = [
      headers.join(","),
      ...rows.map(r => [
        `"${r.source_bucket || ""}"`,
        `"${r.registration_source || ""}"`,
        `"${r.utm_source || ""}"`,
        `"${r.utm_medium || ""}"`,
        `"${r.utm_campaign || ""}"`,
        r.families,
        r.total_members,
      ].join(",")),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `registration-sources-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" /> Registration Sources (Flyer / Social / Website)
        </CardTitle>
        <Button size="sm" variant="outline" disabled={rows.length === 0} onClick={downloadCsv}>
          <Download className="w-4 h-4 mr-2" /> Download CSV
        </Button>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No attribution data captured yet.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={bucketEntries.map(([name, v]) => ({ name, value: v.families }))}
                    dataKey="value" nameKey="name" outerRadius={100} label
                  >
                    {bucketEntries.map(([name], i) => (
                      <Cell key={i} fill={SOURCE_COLORS[name] || COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Families</TableHead>
                    <TableHead className="text-right">Members</TableHead>
                    <TableHead className="text-right">Share</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bucketEntries.map(([name, v]) => (
                    <TableRow key={name}>
                      <TableCell className="font-medium flex items-center gap-2">
                        <span className="inline-block w-3 h-3 rounded-sm" style={{ background: SOURCE_COLORS[name] || "#94A3B8" }} />
                        {name}
                      </TableCell>
                      <TableCell className="text-right">{v.families}</TableCell>
                      <TableCell className="text-right">{v.members}</TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {totalFamilies ? `${Math.round((v.families / totalFamilies) * 100)}%` : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MetricDonut({
  label, value, total, color, unit = "",
}: { label: string; value: number; total: number; color: string; unit?: string }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  const r = 38;
  const c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  return (
    <div
      className="rounded-xl border bg-card p-5 flex flex-col items-center text-center hover:shadow-md transition-shadow"
      title={`${value} ${unit} • ${pct.toFixed(1)}%`}
    >
      <div className="text-sm font-semibold text-foreground mb-3 break-words min-h-[2.5rem] flex items-center">
        {label}
      </div>
      <div className="relative w-[112px] h-[112px]">
        <svg width="112" height="112" viewBox="0 0 112 112" className="-rotate-90">
          <circle cx="56" cy="56" r={r} stroke="hsl(var(--muted))" strokeWidth="10" fill="none" />
          <circle
            cx="56" cy="56" r={r} stroke={color} strokeWidth="10" fill="none"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c - dash}`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-3xl font-extrabold leading-none text-foreground">{value}</div>
          <div className="text-xs font-medium mt-1" style={{ color }}>{pct.toFixed(0)}%</div>
        </div>
      </div>
      {unit && <div className="text-xs text-muted-foreground mt-3 font-medium">{unit}</div>}
    </div>
  );
}


function CategoryCard({
  label, value, total, color, Icon,
}: { label: string; value: number; total: number; color: string; Icon: any }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="rounded-xl border bg-card p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium text-muted-foreground">{label}</div>
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center"
          style={{ background: `${color}1A`, color }}
        >
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-[11px] text-muted-foreground mb-2">{pct.toFixed(1)}% of total</div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
      </div>
    </div>
  );
}

function PaymentChip({
  label, value, total, icon: Icon, color,
}: { label: string; value: number; total: number; icon: any; color: string }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        {label}
      </div>
      <div className="text-xl font-bold mt-1">{value}</div>
      <div className="text-[11px] text-muted-foreground">{pct.toFixed(1)}%</div>
    </div>
  );
}

function KpiCard({ kpi, trend }: { kpi: any; trend: { pct: number; dir: "up" | "down" } | null }) {
  const Icon = kpi.icon;
  const bgMap: Record<string, string> = {
    "text-primary": "bg-primary/10",
    "text-emerald-600": "bg-emerald-50",
    "text-emerald-700": "bg-emerald-50",
    "text-amber-600": "bg-amber-50",
    "text-amber-700": "bg-amber-50",
    "text-rose-600": "bg-rose-50",
    "text-red-600": "bg-red-50",
    "text-sky-600": "bg-sky-50",
    "text-indigo-600": "bg-indigo-50",
    "text-violet-600": "bg-violet-50",
  };
  const clickable = !!kpi.href;
  const handleClick = () => {
    if (!kpi.href) return;
    if (typeof window !== "undefined") window.location.assign(kpi.href);
  };
  return (
    <Card
      className={`transition-shadow ${clickable ? "cursor-pointer hover:shadow-md hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/30" : "hover:shadow-sm"}`}
      onClick={clickable ? handleClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(); } } : undefined}
      title={clickable ? "View details" : undefined}
    >
      <CardContent className="p-3.5">
        <div className="flex items-start gap-2">
          <div className={`w-8 h-8 rounded-md ${bgMap[kpi.tone] || "bg-muted"} flex items-center justify-center shrink-0 ${kpi.tone}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="text-[11px] font-medium text-muted-foreground leading-tight pt-0.5">{kpi.label}</div>
        </div>
        <div className="text-2xl font-bold mt-2">{typeof kpi.value === "string" ? kpi.value : Number(kpi.value).toLocaleString()}</div>
        <div className="text-[10px] text-muted-foreground leading-snug mt-0.5 line-clamp-1">{kpi.hint}</div>
        {trend && (
          <div className={`flex items-center gap-1 text-[10px] mt-1.5 ${trend.dir === "up" ? "text-emerald-600" : "text-rose-600"}`}>
            {trend.dir === "up" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            <span className="font-semibold">{trend.pct}%</span>
            <span className="text-muted-foreground">vs last period</span>
          </div>
        )}
        {clickable && (
          <div className="text-[10px] text-primary/70 mt-1.5 flex items-center gap-1">
            <span>View details</span>
            <ArrowRight className="w-3 h-3" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FunnelSteps({ steps }: { steps: { label: string; value: number; color: string; icon: any }[] }) {
  const palette: Record<string, { bg: string; text: string; ring: string }> = {
    sky:     { bg: "bg-sky-50",     text: "text-sky-700",     ring: "ring-sky-200" },
    amber:   { bg: "bg-amber-50",   text: "text-amber-700",   ring: "ring-amber-200" },
    emerald: { bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200" },
    violet:  { bg: "bg-violet-50",  text: "text-violet-700",  ring: "ring-violet-200" },
  };
  return (
    <div className="flex items-center justify-between gap-1">
      {steps.map((s, i) => {
        const p = palette[s.color];
        const Icon = s.icon;
        return (
          <div key={s.label} className="flex items-center gap-1 flex-1 min-w-0">
            <div className={`flex-1 rounded-lg ${p.bg} ${p.text} ring-1 ${p.ring} p-2.5 text-center`}>
              <Icon className="w-4 h-4 mx-auto mb-1 opacity-80" />
              <div className="text-xl font-bold leading-none">{s.value}</div>
              <div className="text-[10px] mt-1 font-medium opacity-90">{s.label}</div>
            </div>
            {i < steps.length - 1 && <ArrowRight className="w-3 h-3 text-muted-foreground shrink-0" />}
          </div>
        );
      })}
    </div>
  );
}

function RegionMiniBar({
  regionRows, cityRows, isScoped, scopedRegion,
}: { regionRows: RegionRow[]; cityRows: CityRow[]; isScoped: boolean; scopedRegion: string | null }) {
  const data = useMemo(() => {
    const map: Record<string, number> = {};
    cityRows.forEach(c => {
      if (isScoped && scopedRegion && !regionMatches(c.region, scopedRegion)) return;
      map[c.region] = (map[c.region] || 0) + Number(c.families || 0);
    });
    regionRows.forEach(r => {
      if (isScoped && scopedRegion && !regionMatches(r.region_name, scopedRegion)) return;
      if (!(r.region_name in map)) map[r.region_name] = Number(r.families || 0);
    });
    return Object.entries(map)
      .map(([region, value]) => ({ name: region.replace(" Region", ""), value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }, [regionRows, cityRows, isScoped, scopedRegion]);

  if (!data.length) return <p className="text-sm text-muted-foreground py-6 text-center">No regional data.</p>;
  return (
    <div style={{ height: 220 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 18, right: 8, left: 0, bottom: 4 }} barCategoryGap="30%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
          <YAxis allowDecimals={false} tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={24} />
          <Tooltip cursor={{ fill: "hsl(var(--muted))", opacity: 0.3 }} />
          <Bar dataKey="value" fill="#1E3A8A" radius={[6, 6, 0, 0]} maxBarSize={48} label={{ position: "top", fontSize: 10 }} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function RecentActivityCompact({
  regs, cancels, statusFilter,
}: { regs: RecentRegRow[]; cancels: RecentCancelRow[]; statusFilter: string }) {
  type Item = { kind: "registered" | "confirmed" | "pending" | "cancelled"; ts: string; name: string };
  const items: Item[] = [];
  regs.forEach(r => {
    if (statusFilter !== "all" && statusFilter !== r.payment_status) return;
    const kind: Item["kind"] =
      r.payment_status === "confirmed" ? "confirmed" :
      r.payment_status === "pending"   ? "pending"   :
      r.payment_status === "cancelled" ? "cancelled" : "registered";
    items.push({ kind, ts: r.created_at, name: `${r.first_name} ${r.last_name}` });
  });
  cancels.forEach(c => {
    if (statusFilter !== "all" && statusFilter !== "cancelled") return;
    items.push({ kind: "cancelled", ts: c.created_at, name: `Cancellation (${c.status})` });
  });
  items.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
  const top = items.slice(0, 6);
  if (!top.length) return <p className="text-sm text-muted-foreground py-6 text-center">No recent activity.</p>;
  const badgeFor = (k: Item["kind"]) => {
    const map = {
      registered: "bg-sky-50 text-sky-700 border-sky-200",
      confirmed:  "bg-emerald-50 text-emerald-700 border-emerald-200",
      pending:    "bg-amber-50 text-amber-700 border-amber-200",
      cancelled:  "bg-rose-50 text-rose-700 border-rose-200",
    } as const;
    const lbl = { registered: "Registered", confirmed: "Confirmed", pending: "Pending", cancelled: "Cancelled" } as const;
    return <Badge variant="outline" className={`text-[10px] h-5 ${map[k]}`}>{lbl[k]}</Badge>;
  };
  return (
    <ul className="divide-y">
      {top.map((it, i) => (
        <li key={i} className="flex items-center justify-between gap-2 py-2 text-sm">
          <span className="font-medium truncate">{it.name}</span>
          <div className="flex items-center gap-2 shrink-0">
            {badgeFor(it.kind)}
            <span className="text-[10px] text-muted-foreground">{new Date(it.ts).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function PaymentMiniStat({ label, value, total, color }: { label: string; value: number; total: number; color: string }) {
  const pct = total ? (value / total) * 100 : 0;
  return (
    <div className="rounded-md border bg-muted/30 px-2 py-1.5">
      <div className={`text-[10px] font-medium ${color}`}>{label}</div>
      <div className="text-base font-bold leading-tight">{value}</div>
      <div className="text-[10px] text-muted-foreground">{pct.toFixed(1)}%</div>
    </div>
  );
}
